import { apiClient } from "../config";

export interface Student {
  id: number;
  firstName: string;
  middleName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  country: string;
  countryState: string;
  isActive: number;
  lgc: string | null;
  dateOfBirth: string | null;
  timeCreated: string;
  uid: string | null;
  modifiedProfile: boolean;
  modifiedLgc: boolean;
  modifiedDob: boolean;
  qualifiedForAward: boolean;
  creditUnits: number;
}

export interface StudentCourse {
  id: number;
  course: {
    id: number;
    courseName: string;
    courseDescription: string;
  };
  student: Student;
  enrolled: boolean;
  completed: boolean;
  paid: boolean;
  timeCreated: string;
}

export interface AwardCertificate {
  id: number;
  studentCourse: StudentCourse;
  amount: number;
  passed: boolean;
  score: number;
  numWritten: number;
  timeCreated: string;
  timeLastWritten: string;
  paid: boolean;
}

// Hall of Fame category (from /public/getAllHallOfFame)
export interface HallOfFameCategory {
  id: number;
  title: string;
  description: string;
  videoUrl: string | null;
  courseList: {
    id: number;
    courseName: string;
    courseDescription: string;
    participants: number;
    enrolled: number;
  }[];
  staff: {
    id: number;
    firstName: string;
    lastName: string;
  };
  timeCreated: string;
}

// Fetch all hall of fame categories
export const getAllHallOfFameCategories = async (): Promise<
  HallOfFameCategory[]
> => {
  try {
    const { data } = await apiClient.get<HallOfFameCategory[]>(
      "/public/getAllHallOfFame",
    );
    return data || [];
  } catch (error) {
    console.error("Error fetching hall of fame categories:", error);
    return [];
  }
};

// Fetch students for a specific hall of fame
export const getHallOfFameStudents = async (
  hallOfFameId: number,
): Promise<AwardCertificate[]> => {
  try {
    const { data } = await apiClient.get<AwardCertificate[]>(
      `/public/getHallOfFameStudents/${hallOfFameId}`,
    );
    return data || [];
  } catch (error) {
    console.error(
      `Error fetching students for hall of fame ${hallOfFameId}:`,
      error,
    );
    return [];
  }
};

// Legacy: fetch all paid award certificates (still used elsewhere)
export const getAllPaidAwardCertificates = async (): Promise<
  AwardCertificate[]
> => {
  try {
    const { data } = await apiClient.get<AwardCertificate[]>(
      "/public/getAllPaidAwardCertificates",
    );
    return data || [];
  } catch (error) {
    console.error("Error fetching paid award certificates:", error);
    return [];
  }
};

export const getStudentPic = async (
  studentId: number,
): Promise<string | null> => {
  try {
    const response = await apiClient.get(
      `/public/getStudentPics/${studentId}`,
      {
        responseType: "blob",
      },
    );
    return URL.createObjectURL(response.data);
  } catch (error) {
    console.error(`Error fetching picture for student ${studentId}:`, error);
    return null;
  }
};

export const getHallOfFamePicture = async (
  hallOfFameId: number,
): Promise<string | null> => {
  try {
    const response = await apiClient.get(
      `/public/getHallOfFamePicture/${hallOfFameId}`,
      {
        responseType: "blob",
      },
    );
    return URL.createObjectURL(response.data);
  } catch (error) {
    // 404 / no picture set is expected for many categories — fail quietly
    return null;
  }
};
